import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ScrollText } from 'lucide-react'
import { useAllOrderHistory } from '@/hooks/data/useOrderAnalytics'

const STATUS_COLORS: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Refunded: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
}

function AuditLogPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('')
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const cursor = cursorStack[cursorStack.length - 1]

  const filters = useMemo(() => ({ status: status || undefined, cursor, pageSize: 25 }), [status, cursor])
  const history = useAllOrderHistory(filters)

  const resetPaging = () => setCursorStack([])

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <ScrollText className="w-6 h-6" /> Audit Log
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Real order-status change history across every order (OrderService's OrderHistory table) — the only general-purpose audit trail in this system. No IP/user-agent columns and no product/customer-profile edit history exist to show here; this is honestly scoped to order status changes.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            resetPaging()
          }}
          className="mb-3 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">All actions</option>
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Refunded">Refunded</option>
        </select>

        {history.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : history.isError ? (
          <p className="text-sm text-red-600">Failed to load — requires a logged-in manager/admin session.</p>
        ) : !history.data || history.data.items.length === 0 ? (
          <p className="text-sm text-gray-500">No history entries match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="pb-2 pr-4">Timestamp</th>
                  <th className="pb-2 pr-4">Order</th>
                  <th className="pb-2 pr-4">Customer</th>
                  <th className="pb-2 pr-4">Action</th>
                  <th className="pb-2 pr-4">Note</th>
                  <th className="pb-2">Performed by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {history.data.items.map((h) => (
                  <tr key={h.id}>
                    <td className="py-2 pr-4 text-xs text-gray-500">{new Date(h.createdAt).toLocaleString()}</td>
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => navigate({ to: '/orders/$id', params: { id: h.orderId } })}
                        className="text-blue-600 hover:underline"
                      >
                        {h.orderNumber}
                      </button>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs text-gray-600 dark:text-gray-300">{h.customerId.slice(0, 8)}</td>
                    <td className="py-2 pr-4">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[h.status] ?? ''}`}>{h.status}</span>
                    </td>
                    <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{h.note ?? <span className="text-gray-400">—</span>}</td>
                    <td className="py-2 font-mono text-xs text-gray-500">
                      {h.performedBy ? h.performedBy.slice(0, 8) : <span className="italic text-gray-400">system</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 flex items-center gap-3">
          {cursorStack.length > 0 && (
            <button onClick={() => setCursorStack((prev) => prev.slice(0, -1))} className="text-sm text-blue-600 hover:underline">
              ← Newer
            </button>
          )}
          {history.data?.hasMore && history.data.nextCursor && (
            <button
              onClick={() => setCursorStack((prev) => [...prev, history.data!.nextCursor!])}
              className="text-sm text-blue-600 hover:underline"
            >
              Older →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/audit-log/')({ component: AuditLogPage })
